import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import pointLayerService from "../../../../services/web/point-layer-service.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilter = {
    name: "",
}

const PointLayerList = () => {
    const navigate = navigateHook();
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const [filter, setFilter] = useState(initialFilter);


    const [columnDefs] = useState([
        {headerName: "Tipo", field: "icon_name", width: 50,
            cellRenderer: ({value}) => {
                return <div className={"flex h-full items-center"}>
                    <img src={`${API_URL}/pointLayer/marker/${value}`} alt="" className={"h-6 w-full object-contain"}/>
                </div>
            }
        },
        {headerName: "Nome", field: "name", flex:1, filter: "agTextColumnFilter", sort: "asc",
            filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
    ]);

    const {data, isLoading} = useQuery({
        queryKey: ['point-layer-list'],
        queryFn: async () => pointLayerService.list().then(({data}) => data),
    });

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = ({event, data}) => {
        const id = data.id;
        const urlDev = `/point-layer/form/${id}`;
        let isBlank = false
        if (event.ctrlKey) isBlank = true

        navigate(urlDev, isBlank)
    }

    const openForm = () => {
        const urlDev = `/point-layer/form`;
        navigate(urlDev)
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Pontos</Template.Title>
                <Button onClick={openForm}>
                    <FaPlus/> Adicionar ponto
                </Button>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className="col-span-12 flex gap-4">
                                <InputForm id="name" name="name" value={filter.name} onChange={onChange} label="Nome" classNameWrapper={"w-full"}/>
                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={data || []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                onRowClicked={onRowClicked}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default PointLayerList;